import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import useFetchWithMsal from '../../../hooks/useFetchWithMsal';
import { useLineMapping } from '../useLineMapping';
import { protectedResources } from '../../../authConfig';
import { cleanMappings } from '../../../Utils/purchaseInvoices/approvalPackages/approvalPackageDetailsUtils';

export const useApprovalPackageDetails = (data, refetchData, onClose) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  
  const [showLineMappingMode, setShowLineMappingMode] = useState(false);
  const [lineMappingCheckResult, setLineMappingCheckResult] = useState(null);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(false);
  const [displayMappingNumbers, setDisplayMappingNumbers] = useState(false);
  const [mappingSource, setMappingSource] = useState(null);
  const [lineMappingCompleted, setLineMappingCompleted] = useState(data?.lineMappingCompleted || false);
  
  const aiMappingLoadedRef = useRef(false);
  const hasLoadedForIdRef = useRef(null);
  
  const { 
    loading: lineMappingLoading, 
    checkLineMappingRequired,
    getAiSuggestedMapping,
    approveAiMapping,
    error: lineMappingError 
  } = useLineMapping();

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  useEffect(() => {
    if (data?.lineMappingCompleted !== undefined) {
      setLineMappingCompleted(data.lineMappingCompleted);
      if (data.lineMappingCompleted) {
        aiMappingLoadedRef.current = true;
        hasLoadedForIdRef.current = data._id;
      }
    }
  }, [data?.lineMappingCompleted]);

  useEffect(() => {
    const alreadyLoadedForThisId = hasLoadedForIdRef.current === data?._id;
    
    if (data?._id && data?.requiresLineMapping && !alreadyLoadedForThisId && mappings.length === 0) {
      console.log('Need to load mapping for ID:', data._id);
      loadMappingData();
    } else {
      console.log('Skipping mapping load:', { 
        hasId: !!data?._id,
        requiresLineMapping: data?.requiresLineMapping,
        alreadyLoadedForThisId,
        hasMappings: mappings.length > 0
      });
    }
  }, [data?._id, data?.requiresLineMapping, lineMappingCompleted]);

  const loadMappingData = async () => {
    if (!data?._id || aiMappingLoadedRef.current || hasLoadedForIdRef.current === data._id) {
      console.log('Skipping mapping data load (already loaded)');
      return;
    }

    console.log('Loading mapping data for ID:', data._id);
    
    try {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${data._id}/line-mapping`
      );

      if (response?.status === 'SUCCESS' && response.data?.lineMapping && response.data.lineMapping.length > 0) {
        console.log('Found saved mapping, loading it');
        setMappings(response.data.lineMapping);
        setDisplayMappingNumbers(true);
        setLineMappingCompleted(response.data.lineMappingCompleted || true);
        const isAiMapping = response.data.aiMappingApproved || false;
        setMappingSource(isAiMapping ? 'AI' : 'MANUAL');
        aiMappingLoadedRef.current = true;
        hasLoadedForIdRef.current = data._id;
        return;
      }

      console.log('No saved mapping, loading AI suggestion');
      await loadAiSuggestion();
      
    } catch (error) {
      console.error('Error loading mapping data:', error);
      await loadAiSuggestion();
    }
  };

  const loadAiSuggestion = async () => {
    if (!data?._id || aiMappingLoadedRef.current || hasLoadedForIdRef.current === data._id || lineMappingCompleted) {
      console.log('Skipping AI suggestion load (already loaded):', { 
        hasId: !!data?._id, 
        aiMappingLoaded: aiMappingLoadedRef.current,
        hasLoadedForId: hasLoadedForIdRef.current,
        currentId: data._id,
        lineMappingCompleted 
      });
      return;
    }
    
    console.log('Loading AI suggestion for the first time for ID:', data._id);
    setLoadingAiSuggestion(true);
    try {
      const suggestion = await getAiSuggestedMapping(data._id);
      if (suggestion?.aiSuggestedMapping) {
        setAiSuggestion(suggestion.aiSuggestedMapping);
        setDisplayMappingNumbers(true);
        setMappingSource('AI');
        aiMappingLoadedRef.current = true;
        hasLoadedForIdRef.current = data._id;
        console.log('AI suggestion loaded successfully for ID:', data._id);
      }
    } catch (error) {
      console.error('Error loading AI suggestion:', error);
    } finally {
      setLoadingAiSuggestion(false);
    }
  };

  const handleEditAiMapping = async () => {
    setShowLineMappingMode(true);
    setDisplayMappingNumbers(true);
    
    console.log('Starting edit mode - clearing old mappings and fetching fresh data...');
    
    setMappings([]);
    
    try {
      if (typeof refetchData === 'function') {
        console.log('Refetching data to get updated invoice items...');
        await refetchData();
      }
      
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${data._id}/line-mapping`
      );
      
      if (response?.status === 'SUCCESS' && response.data?.lineMapping && response.data.lineMapping.length > 0) {
        console.log('Loaded saved mapping from DB:', response.data.lineMapping);
        setMappings(response.data.lineMapping);
      } else if (aiSuggestion?.mappings) {
        console.log('Using AI suggestion as fallback:', aiSuggestion.mappings);
        setMappings(aiSuggestion.mappings);
      } else {
        console.log('No mappings found - starting fresh');
      }
    } catch (error) {
      console.error('Error loading mapping for edit:', error);
      enqueueSnackbar('שגיאה בטעינת השיוך לעריכה', { variant: 'error' });
    }
  };

  const handleApproveAiMapping = async () => {
    if (!aiSuggestion?.mappings || aiSuggestion.mappings.length === 0) {
      enqueueSnackbar('אין הצעת שיוך לאישור', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const result = await approveAiMapping(data._id, aiSuggestion.mappings);
      
      if (result.success) {
        enqueueSnackbar('הצעת השיוך אושרה בהצלחה! החשבונית הועברה לממתין לאישור', { variant: 'success' });
        
        setLineMappingCompleted(true);
        setMappings(aiSuggestion.mappings);
        setMappingSource('AI');
        
        aiMappingLoadedRef.current = false;
        hasLoadedForIdRef.current = null;
        
        if (typeof refetchData === 'function') {
          await refetchData();
        }
        
        queryClient.invalidateQueries(['approvalPackages']);
        queryClient.invalidateQueries(['spInvoices']);
        
        setTimeout(() => {
          if (typeof onClose === 'function') {
            onClose();
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error approving AI mapping:', error);
      enqueueSnackbar('שגיאה באישור השיוך', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getOrderItemMappingNumber = (orderIndex) => {
    if (!displayMappingNumbers) return null;
    
    const mappingIndex = mappings.findIndex(
      m => m.orderLineIndex === orderIndex
    );
    
    if (mappingIndex !== -1) return mappingIndex + 1;
    
    if (aiSuggestion?.mappings) {
      const aiMappingIndex = aiSuggestion.mappings.findIndex(
        m => m.orderLineIndex === orderIndex
      );
      return aiMappingIndex !== -1 ? aiMappingIndex + 1 : null;
    }
    
    return null;
  };

  const getInvoiceItemMappingNumbers = (invoiceIndex) => {
    if (!displayMappingNumbers) return [];
    
    const mappingNumbers = [];
    
    mappings.forEach((mapping, idx) => {
      if (mapping.invoiceLineIndices.includes(invoiceIndex)) {
        mappingNumbers.push(idx + 1);
      }
    });
    
    if (mappingNumbers.length === 0 && aiSuggestion?.mappings) {
      aiSuggestion.mappings.forEach((mapping, idx) => {
        if (mapping.invoiceLineIndices.includes(invoiceIndex)) {
          mappingNumbers.push(idx + 1);
        }
      });
    }
    
    return mappingNumbers;
  };

  const handleCheckLineMapping = async () => {
    if (!data?._id) return;
    
    try {
      const result = await checkLineMappingRequired(data._id);
      setLineMappingCheckResult(result);
      
      if (result?.requiresLineMapping) {
        setShowLineMappingMode(true);
        await loadExistingMapping();
      }
    } catch (error) {
      console.error('Error checking line mapping requirement:', error);
    }
  };

  const loadExistingMapping = async () => {
    if (!data?._id) return;
    
    setLoadingMapping(true);
    try {
      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${data._id}/line-mapping`
      );

      if (response?.status === 'SUCCESS' && response.data?.lineMapping) {
        setMappings(response.data.lineMapping);
      }
    } catch (error) {
      console.error('Error loading existing mapping:', error);
      enqueueSnackbar('שגיאה בטעינת השיוך הקיים', { variant: 'error' });
    } finally {
      setLoadingMapping(false);
    }
  };

  const enterLineMappingMode = () => {
    setShowLineMappingMode(true);
    setDisplayMappingNumbers(true);
    loadExistingMapping();
  };

  const exitLineMappingMode = () => {
    setShowLineMappingMode(false);
    setSelectedOrderIndex(null);
  };

  const handleOrderItemClick = (orderIndex) => {
    setSelectedOrderIndex(selectedOrderIndex === orderIndex ? null : orderIndex);
  };

  const handleInvoiceItemClick = (invoiceIndex) => {
    if (selectedOrderIndex === null) {
      enqueueSnackbar('בחר תחילה שורה בהזמנה', { variant: 'warning' });
      return;
    }

    const existingMapping = mappings.find(m => m.orderLineIndex === selectedOrderIndex);
    
    if (existingMapping) {
      if (!existingMapping.invoiceLineIndices.includes(invoiceIndex)) {
        const updatedMappings = mappings.map(mapping => 
          mapping.orderLineIndex === selectedOrderIndex
            ? { 
                ...mapping, 
                invoiceLineIndices: [...mapping.invoiceLineIndices, invoiceIndex].sort((a, b) => a - b)
              }
            : mapping
        );
        setMappings(updatedMappings);
        enqueueSnackbar('שורה נוספה לשיוך', { variant: 'success' });
      } else {
        enqueueSnackbar('השורה כבר משויכת', { variant: 'info' });
      }
    } else {
      const newMapping = {
        orderLineIndex: selectedOrderIndex,
        invoiceLineIndices: [invoiceIndex],
        notes: ''
      };
      setMappings([...mappings, newMapping]);
      enqueueSnackbar('שיוך חדש נוצר', { variant: 'success' });
    }
  };

  const removeMapping = (orderIndex, invoiceIndex = null) => {
    if (invoiceIndex !== null) {
      const updatedMappings = mappings.map(mapping => 
        mapping.orderLineIndex === orderIndex
          ? {
              ...mapping,
              invoiceLineIndices: mapping.invoiceLineIndices.filter(idx => idx !== invoiceIndex)
            }
          : mapping
      ).filter(mapping => mapping.invoiceLineIndices.length > 0);
      
      setMappings(updatedMappings);
    } else {
      setMappings(mappings.filter(m => m.orderLineIndex !== orderIndex));
    }
  };

  const isInvoiceItemMapped = (invoiceIndex) => {
    return mappings.some(mapping => 
      mapping.invoiceLineIndices.includes(invoiceIndex)
    );
  };

  const getInvoiceItemMapping = (invoiceIndex) => {
    return mappings.find(mapping => 
      mapping.invoiceLineIndices.includes(invoiceIndex)
    );
  };

  const saveMapping = async () => {
    if (mappings.length === 0) {
      enqueueSnackbar('לא הוגדר שיוך לשמירה', { variant: 'warning' });
      return;
    }

    const orderItemsCount = data?.order?.items?.length || 0;
    const invoiceItemsCount = data?.invoiceId?.items?.length || 0;
    
    console.log('Validation Info:');
    console.log('Order items count:', orderItemsCount);
    console.log('Invoice items count:', invoiceItemsCount);
    console.log('Current mappings:', JSON.stringify(mappings, null, 2));
    
    const cleanedMappings = cleanMappings(mappings, orderItemsCount, invoiceItemsCount);

    console.log('Cleaned mappings:', JSON.stringify(cleanedMappings, null, 2));

    if (cleanedMappings.length === 0) {
      enqueueSnackbar('לא נמצאו שיוכים תקינים לשמירה', { variant: 'warning' });
      return;
    }

    if (cleanedMappings.length < mappings.length) {
      const removedCount = mappings.length - cleanedMappings.length;
      console.log(`Cleaned ${removedCount} invalid mappings`);
      enqueueSnackbar(
        `הוסרו ${removedCount} שיוכים לא תקינים. ממשיך עם ${cleanedMappings.length} שיוכים תקינים.`,
        { variant: 'warning' }
      );
      setMappings(cleanedMappings);
    }

    setSaving(true);
    try {
      console.log('Sending to server:', { lineMapping: cleanedMappings });
      
      const response = await execute(
        'POST',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/line-mapping/${data._id}/line-mapping`,
        { lineMapping: cleanedMappings }
      );

      if (response?.status === 'SUCCESS') {
        enqueueSnackbar('השיוך נשמר בהצלחה! החשבונית הועברה לממתין לאישור', { variant: 'success' });
        
        setShowLineMappingMode(false);
        setSelectedOrderIndex(null);
        setLineMappingCompleted(true);
        setMappingSource('MANUAL');
        
        aiMappingLoadedRef.current = false;
        hasLoadedForIdRef.current = null;
        
        if (aiSuggestion) {
          setAiSuggestion({
            ...aiSuggestion,
            mappings: cleanedMappings
          });
        }
        
        if (typeof refetchData === 'function') {
          await refetchData();
        }
        
        queryClient.invalidateQueries(['approvalPackages']);
        queryClient.invalidateQueries(['spInvoices']);
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      enqueueSnackbar('שגיאה בשמירת השיוך', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const clearMapping = () => {
    if (mappings.length === 0) {
      enqueueSnackbar('אין שיוכים לניקוי', { variant: 'info' });
      return;
    }
    
    if (window.confirm(`האם אתה בטוח שברצונך לנקות את כל ${mappings.length} השיוכים?`)) {
      setMappings([]);
      setSelectedOrderIndex(null);
      enqueueSnackbar('כל השיוכים נוקו', { variant: 'success' });
    }
  };

  return {
    showLineMappingMode,
    lineMappingCheckResult,
    selectedOrderIndex,
    mappings,
    saving,
    loadingMapping,
    aiSuggestion,
    loadingAiSuggestion,
    displayMappingNumbers,
    mappingSource,
    lineMappingCompleted,
    lineMappingLoading,
    lineMappingError,
    aiMappingLoadedRef,
    handleEditAiMapping,
    handleApproveAiMapping,
    getOrderItemMappingNumber,
    getInvoiceItemMappingNumbers,
    handleCheckLineMapping,
    enterLineMappingMode,
    exitLineMappingMode,
    handleOrderItemClick,
    handleInvoiceItemClick,
    removeMapping,
    isInvoiceItemMapped,
    getInvoiceItemMapping,
    saveMapping,
    clearMapping
  };
};
